import { useState } from 'react';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import {
  getPrincipalName,
  MATTER_SELECTION_PAGE_SIZE,
  MATTER_SELECTION_QUERY,
  toMatterOption,
} from '../dashboard/addTransaction/matterSelection';
import { useWorkTime } from '../../context/WorkTimeContext';
import { useFetchMatters } from '../../hooks/useFetchMatters';
import { useOwnerSelectFilter } from '../../hooks/useOwnerSelectFilter';
import { IMatter } from '../../interface/matter';
import { IWorkTime } from '../../interface/workTimes';

/** The props `SearchableSelect` needs to pick a work time's matter, from the same list the picker screen uses. */
export const useWorkTimeMatterSelect = () => {
  const { workTime, updateWorkTime } = useWorkTime();
  const [searchValue, setSearchValue] = useState('');
  const { isMyOwn, filters } = useOwnerSelectFilter();
  const {
    data: matters,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchMatters({
    ...MATTER_SELECTION_QUERY,
    sortBy: ['openingDate:DESC'],
    pageSize: MATTER_SELECTION_PAGE_SIZE,
    search: searchValue,
    myOwn: isMyOwn,
  });

  const options: SelectOption[] = matters.map((matter: IMatter) =>
    toMatterOption(matter, getPrincipalName(matter)),
  );
  // A saved work time is loaded with its matter and its principal as separate relations.
  const value: SelectOption | null = workTime?.matter?.id
    ? toMatterOption(
        workTime.matter,
        getPrincipalName({
          principalCompany: workTime.matter.principalCompany ?? workTime.company,
          principalPrivatePerson: workTime.matter.principalPrivatePerson ?? workTime.privatePerson,
        }),
      )
    : null;

  const onChange = (option: SelectOption) => {
    const matter = matters.find(({ id }: IMatter) => id === option.value);
    if (!matter) return;
    updateWorkTime({ ...workTime, matter, matterId: matter.id } as IWorkTime);
  };

  return {
    options,
    value,
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
    filters,
  };
};
